import crypto from 'crypto'

import { parseJsonString } from '@leid/helpers'

export const ORGANIZATION_HASH_COOKIE_NAME = 'AuthOrganizationInfo'
export const COORDINATES_HASH_COOKIE_NAME = 'CoordinatesInfo'

export type OrganizationCookieData = {
  customerId: number
  organizationId: number
}

export type CoordinatesCookieData = {
  lat: number | undefined
  long: number | undefined
  label: string | undefined
}

export type LangCookieData = {
  language: string | undefined
}

const _algo = 'aes-256-gcm'
const _authTagByteLength = 16
const _ivByteLength = 12

export const encryptOrganizationCookie = (customerId: number, organizationId: number): string | undefined => {
  try {
    const data: OrganizationCookieData = {
      customerId: customerId,
      organizationId: organizationId,
    }

    const messageText = JSON.stringify(data)

    const iv = getIV()
    const keyInBytes = readKeyInBytes()

    const cipher = crypto.createCipheriv(_algo, keyInBytes, iv, {
      authTagLength: _authTagByteLength,
    })
    let encryptedMessage = cipher.update(messageText)
    encryptedMessage = Buffer.concat([encryptedMessage, cipher.final()])
    return Buffer.concat([iv, encryptedMessage, cipher.getAuthTag()]).toString('base64')
  } catch (error) {
    console.log(`Error on encrypting cookie data`, error, customerId, organizationId)
    return undefined
  }
}

export const decryptOrganizationCookie = (cookie: string | undefined): OrganizationCookieData | null | undefined => {
  if (!cookie) {
    return null
  }

  try {
    const ciphertext = Buffer.from(cookie, 'base64')

    const authTag = ciphertext.slice(_authTagByteLength * -1)
    const iv = ciphertext.slice(0, _ivByteLength)

    const encryptedMessage = ciphertext.slice(_ivByteLength, _authTagByteLength * -1)

    const keyInBytes = readKeyInBytes()

    const decipher = crypto.createDecipheriv(_algo, keyInBytes, iv, {
      authTagLength: _authTagByteLength,
    })

    decipher.setAuthTag(authTag)

    let messageText = decipher.update(encryptedMessage)
    messageText = Buffer.concat([messageText, decipher.final()])

    const descryptedString = messageText.toString('utf8')

    const result = parseJsonString<OrganizationCookieData>(descryptedString)

    return result
  } catch (error) {
    console.log(`Error on decrypting cookie data`, error, cookie, process.env.ENCRYPTION_KEY)
    return undefined
  }
}

export const encryptCoordinatesCookies = (
  lat: number | undefined,
  long: number | undefined,
  label: string | undefined,
): string | undefined => {
  try {
    const data: CoordinatesCookieData = {
      lat,
      long,
      label,
    }

    const messageText = JSON.stringify(data)

    const iv = getIV()
    const keyInBytes = readKeyInBytes()

    const cipher = crypto.createCipheriv(_algo, keyInBytes, iv, {
      authTagLength: _authTagByteLength,
    })
    let encryptedMessage = cipher.update(messageText)
    encryptedMessage = Buffer.concat([encryptedMessage, cipher.final()])
    return Buffer.concat([iv, encryptedMessage, cipher.getAuthTag()]).toString('base64')
  } catch (error) {
    console.log(`Error on encrypting cookie data`, error, lat, long, label)
    return undefined
  }
}

export const decryptCoordinatesCookie = (cookie: string | undefined): CoordinatesCookieData | undefined => {
  if (!cookie) {
    return undefined
  }

  try {
    const ciphertext = Buffer.from(cookie, 'base64')

    const authTag = ciphertext.slice(_authTagByteLength * -1)
    const iv = ciphertext.slice(0, _ivByteLength)

    const encryptedMessage = ciphertext.slice(_ivByteLength, _authTagByteLength * -1)

    const keyInBytes = readKeyInBytes()

    const decipher = crypto.createDecipheriv(_algo, keyInBytes, iv, {
      authTagLength: _authTagByteLength,
    })

    decipher.setAuthTag(authTag)

    let messageText = decipher.update(encryptedMessage)
    messageText = Buffer.concat([messageText, decipher.final()])

    const descryptedString = messageText.toString('utf8')

    const result = parseJsonString<CoordinatesCookieData>(descryptedString)

    return result
  } catch (error) {
    console.log(`Error on decrypting cookie data`, error, cookie, process.env.ENCRYPTION_KEY)
    return undefined
  }
}

const getIV = (): Buffer => {
  return crypto.randomBytes(_ivByteLength)
}

const readKeyInBytes = (): Buffer => {
  return Buffer.from(process.env.ENCRYPTION_KEY?.toString() ?? '', 'base64')
}
